import { useMutation } from '@apollo/client';
import { createError, getStandardErrors } from '@common/neo.tax';
import { AlertModalLayout, Button } from '@ui-components/neo.tax';
import { useState } from 'react';

import {
  DataSourcesTableItem,
  DeleteRdBusinessComponentUploadMutation,
  DeleteRdBusinessComponentUploadMutationVariables,
  DELETE_RD_BUSINESS_COMPONENT_UPLOAD,
  GetRdBusinessComponentUploadsQueryVariables,
  GET_RD_BUSINESS_COMPONENT_UPLOADS,
} from '@neo.tax/shared';

import { StyledGeneralFormError, StyledModalContainer } from './delete-rd-business-component-upload.styled';

interface DeleteRdBusinessComponentUploadProps {
  fiscalYearId: string;
  itemToDelete: DataSourcesTableItem;
  close: () => void;
}

const FALLBACK_ERROR_MESSAGE = 'Something unexpected prevented the file from being deleted. Please try again.';

/** The server words its refusals for the customer, so those are shown as sent. */
const toErrorMessage = (error: unknown): string => {
  try {
    const [standardError] = getStandardErrors(error);
    return standardError?.description ?? FALLBACK_ERROR_MESSAGE;
  } catch {
    return FALLBACK_ERROR_MESSAGE;
  }
};

export const DeleteRdBusinessComponentUpload = ({
  close,
  fiscalYearId,
  itemToDelete,
}: DeleteRdBusinessComponentUploadProps): React.ReactElement => {
  if (itemToDelete.itemType !== 'rd-business-components-file') {
    throw createError(
      'DeleteRdBusinessComponentUploadError',
      'This component can only be used to delete a business components file.',
    );
  }
  const fileUploadId = itemToDelete.id;

  const [deleteRdBusinessComponentUpload, { loading }] = useMutation<
    DeleteRdBusinessComponentUploadMutation,
    DeleteRdBusinessComponentUploadMutationVariables
  >(DELETE_RD_BUSINESS_COMPONENT_UPLOAD);

  const [errorMessage, setErrorMessage] = useState<string>();

  const onSubmit = async (): Promise<void> => {
    try {
      await deleteRdBusinessComponentUpload({
        variables: { fiscalYearId, fileUploadId },
        refetchQueries: [
          {
            query: GET_RD_BUSINESS_COMPONENT_UPLOADS,
            variables: { fiscalYearId } satisfies GetRdBusinessComponentUploadsQueryVariables,
          },
        ],
        awaitRefetchQueries: true,
      });
      close();
    } catch (error) {
      const message = toErrorMessage(error);
      setErrorMessage(message);
    }
  };

  return (
    <StyledModalContainer>
      <AlertModalLayout
        title={`Delete ${itemToDelete.title}`}
        copy={`Are you sure you want to delete ${itemToDelete.title}? Its business components will be removed with it.`}
        trailingContent={
          <>
            <Button size="base" variant="white" rounded="regular" onClick={close}>
              Cancel
            </Button>
            <Button disabled={loading} rounded="regular" size="base" type="submit" variant="rsbry" onClick={onSubmit}>
              {loading ? 'Deleting...' : 'Confirm'}
            </Button>
          </>
        }
      >
        <StyledGeneralFormError centered error={!!errorMessage}>
          {errorMessage}
        </StyledGeneralFormError>
      </AlertModalLayout>
    </StyledModalContainer>
  );
};
